import * as React from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export type RecurringGift = {
  id: number
  amount: string
  frequency: 'monthly' | 'annual'
  next_charge_on: string
  status: 'active' | 'paused' | 'disabled' | 'ended'
  paused_until?: string | null
  stopped_by: 'donor' | null
  card: { brand: string; last4: string }
  campaign: string
}

export function EditGift({
  draft,
  setDraft,
  onSave,
  onCancel,
  error,
}: {
  draft: Partial<RecurringGift>
  setDraft: React.Dispatch<React.SetStateAction<Partial<RecurringGift>>>
  onSave: () => void
  onCancel: () => void
  error: Error | null
}) {
  return (
    <div className="space-y-3 border-b border-border px-4 py-4">
      <label htmlFor="recurring-gift-amount" className="block text-caption text-muted-foreground">
        Amount
        <Input
          id="recurring-gift-amount"
          aria-label="Amount"
          className="mt-1 h-9"
          value={draft.amount ?? ''}
          onChange={(event) => setDraft((current) => ({ ...current, amount: event.target.value }))}
        />
      </label>
      <div className="block text-caption text-muted-foreground">
        Frequency
        <Select
          value={draft.frequency ?? 'monthly'}
          onValueChange={(value) =>
            setDraft((current) => ({ ...current, frequency: value as RecurringGift['frequency'] }))
          }
        >
          <SelectTrigger className="mt-1 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label
        htmlFor="recurring-gift-next-charge"
        className="block text-caption text-muted-foreground"
      >
        Next charge date
        <Input
          id="recurring-gift-next-charge"
          aria-label="Next charge date"
          type="date"
          className="mt-1 h-9"
          value={draft.next_charge_on ?? ''}
          onChange={(event) =>
            setDraft((current) => ({ ...current, next_charge_on: event.target.value }))
          }
        />
      </label>
      <label htmlFor="recurring-gift-campaign" className="block text-caption text-muted-foreground">
        Campaign
        <Input
          id="recurring-gift-campaign"
          aria-label="Campaign"
          className="mt-1 h-9"
          value={draft.campaign ?? ''}
          onChange={(event) =>
            setDraft((current) => ({ ...current, campaign: event.target.value }))
          }
        />
      </label>
      {error && <Alert>{error.message}</Alert>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" onClick={onSave}>
          Save
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export function CancelDialog({
  open,
  setOpen,
  frequency,
  cancel,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  frequency: string
  cancel: { isError: boolean; isPending: boolean; mutate: () => void }
}) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel your {frequency.toLowerCase()} gift?</DialogTitle>
          <DialogDescription>
            No more charges will be made. Your giving history and receipts stay available.
          </DialogDescription>
        </DialogHeader>
        {cancel.isError && <Alert>We couldn’t cancel your gift. Please try again.</Alert>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Keep giving
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {cancel.isPending ? 'Cancelling...' : 'Cancel gift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
